import { Link } from '@backstage/core-components';
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

export const OnboardingGuide = () => {
  const theme = useTheme();

  return (
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        Getting Started Guide
      </Typography>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="what-is-backstage-content"
          id="what-is-backstage-header"
        >
          <Typography>🏠 What is Backstage?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              <strong>Backstage is an open-source developer portal</strong> that
              brings order to your microservices and infrastructure ecosystem.
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🎯 What Backstage Does
            </Typography>
            <Typography variant="body2" component="div">
              <ul>
                <li>
                  <strong>Centralizes everything:</strong> One place to find all
                  services, documentation, and tools
                </li>
                <li>
                  <strong>Tracks ownership:</strong> Know who maintains what and
                  how to contact them
                </li>
                <li>
                  <strong>Simplifies discovery:</strong> Search across all your
                  company's software assets
                </li>
                <li>
                  <strong>Standardizes workflows:</strong> Consistent templates
                  and processes for all teams
                </li>
              </ul>
            </Typography>

            <Box mt={2} mb={1}>
              <iframe
                width="480"
                height="270"
                src="https://www.youtube.com/embed/85TQEpNCaU0"
                title="What is Backstage?"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  maxWidth: '100%',
                  borderRadius: '4px',
                }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="search-content"
          id="search-header"
        >
          <Typography>🔍 Discovering & Searching</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              Your one-stop place to find everything in Schibsted -
              documentation, components, APIs, and development tools.
            </Typography>

            <Typography variant="body1" paragraph style={{ marginTop: '16px' }}>
              <strong>Key search tips:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              <ul>
                <li>
                  <strong>Filter by type:</strong> "Documentation" for docs,
                  "Component" for services and libraries
                </li>
                <li>
                  <strong>Search by team:</strong> Use your team name to see
                  everything from your group
                </li>
                <li>
                  <strong>Check lifecycle:</strong> "production" = ready to use,
                  "experimental" = in development
                </li>
              </ul>
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '4px',
              }}
            >
              💡 <strong>Pro tip:</strong> Start by typing your team name or
              product - you'll see all related documentation and components.
            </Typography>
            <Typography variant="body2" style={{ marginTop: '12px' }}>
              <Link to="/search">🔍 Try Advanced Search</Link>
              {' | '}
              <Link to="/catalog">📋 Browse Software Catalog</Link>
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="adding-content"
          id="adding-header"
        >
          <Typography>📝 Adding Components to the Catalog</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              Register your software components in the Backstage catalog to
              improve discoverability and provide essential metadata for your
              teams:
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🛠️ Step-by-Step Setup
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>
                Step 1: Give Backstage access to your GitHub organization
              </strong>
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>Install GitHub app on your organization</li>
                <li>
                  Configure automatic scanning for catalog-info.yaml files
                </li>
                <li>Optionally set up webhook for immediate updates</li>
              </ul>
            </Typography>
            <Typography
              variant="body2"
              style={{ marginLeft: '16px', marginBottom: '16px' }}
            >
              <a
                href="https://backstage.schibsted.media/docs/default/component/schibsted-backstage/how-to-guides/how-to-give-backstage-access-to-a-github-organization/"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                📖 Full GitHub Setup Guide
              </a>
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>Step 2: Add your component to the catalog</strong>
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  Use our catalog template to create a PR with catalog-info.yaml
                </li>
                <li>Merge catalog-info.yaml file to the main branch</li>
                <li>Entity appears in the catalog after roughly 1 hour</li>
              </ul>
            </Typography>
            <Typography
              variant="body2"
              style={{ marginLeft: '16px', marginBottom: '16px' }}
            >
              <a
                href="https://backstage.schibsted.media/docs/default/component/schibsted-backstage/how-to-guides/how-to-add-an-entity-to-the-backstage-software-catalog/"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                📖 Full Setup Guide
              </a>
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>Step 3: Publish documentation</strong>
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>Add /docs folder with markdown files to your repo</li>
                <li>
                  Update catalog-info.yaml with techdocs annotation (already
                  there if you have used our template)
                </li>
                <li>
                  Add GitHub Action workflow for auto-publishing (already there
                  if you have used our template)
                </li>
                <li>Use our ready-made action for seamless integration</li>
              </ul>
            </Typography>
            <Typography
              variant="body2"
              style={{ marginLeft: '16px', marginBottom: '16px' }}
            >
              <a
                href="https://backstage.schibsted.media/docs/default/component/schibsted-backstage/how-to-guides/how-to-publish-documentation-to-backstage/"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                📖 Documentation Publishing Guide
              </a>
              {' | '}
              <a
                href="https://schibsted.ghe.com/connect/github-actions/tree/master/publish-techdocs"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                🤖 Ready-to-use GitHub Action
              </a>
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginLeft: '16px',
                marginBottom: '16px',
                padding: '8px',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderRadius: '4px',
              }}
            >
              💡 The GitHub Action automatically handles Python/Node.js setup,
              installs mkdocs-techdocs-core, configures AWS credentials, and
              publishes your docs to Backstage with zero configuration required.
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🔄 Automatic Discovery
            </Typography>
            <Typography variant="body2" paragraph>
              We automatically scan Schibsted organization hourly for
              catalog-info.yaml files - they'll appear in the catalog within 30
              minutes.
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '4px',
              }}
            >
              💡 <strong>Pro tip:</strong> Each catalog entry tracks ownership,
              dependencies, APIs, documentation, and deployment status -
              creating a comprehensive view of your software ecosystem.
            </Typography>
            <Typography variant="body2" style={{ marginTop: '12px' }}>
              <Link to="/create/templates/default/schibsted-backstage-catalog-info">
                📝 Use Catalog Template
              </Link>
              {' | '}
              <Link to="/catalog-import">⚡ Import Existing Component</Link>
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="components-content"
          id="components-header"
        >
          <Typography>📦 Understanding the Software Catalog</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              The Software Catalog is your centralized inventory of all software
              assets at Schibsted - think of it as the "phone book" for your
              tech ecosystem:
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              📋 What's Tracked in Our Catalog
            </Typography>
            <Typography variant="body2" component="div">
              <ul>
                <li>
                  <strong>Services:</strong> APIs, microservices, and backend
                  applications with deployment status and health monitoring
                </li>
                <li>
                  <strong>Websites:</strong> Frontend applications, marketing
                  sites, and user-facing interfaces
                </li>
                <li>
                  <strong>Libraries:</strong> Shared npm packages, SDKs, and
                  reusable code components
                </li>
                <li>
                  <strong>Resources:</strong> Databases, S3 buckets, Kubernetes
                  clusters, and infrastructure components
                </li>
              </ul>
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🏷️ Schibsted-Specific Metadata & Standards
            </Typography>
            <Typography variant="body2" paragraph>
              Each component in our catalog follows Schibsted standards for
              consistency and compliance:
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  <strong>Business Criticality:</strong> Services are classified
                  by their impact on business operations and editorial mission
                </li>
                <li>
                  <strong>Ownership:</strong> Clear team responsibility aligned
                  with our organizational structure
                </li>
                <li>
                  <strong>Lifecycle Management:</strong> Transparent status from
                  experimental to production to deprecated
                </li>
                <li>
                  <strong>Component Types:</strong> Standardized classification
                  (services, libraries, websites, resources)
                </li>
              </ul>
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🔗 Getting Your Component Catalog-Ready
            </Typography>
            <Typography variant="body2" paragraph>
              When adding components to the catalog, focus on providing accurate
              basic information first:
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  Choose the right component type that matches your software
                  asset
                </li>
                <li>
                  Assign ownership to an existing team in our organization
                </li>
                <li>
                  Set the appropriate lifecycle stage for user expectations
                </li>
                <li>
                  For services, define business criticality level for
                  operational planning
                </li>
              </ul>
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '4px',
              }}
            >
              💡 <strong>Pro tip:</strong> Use our catalog template when
              registering new components - it automatically follows Schibsted
              standards and includes all required fields. Browse existing
              components in the catalog to see examples of proper metadata for
              your type of service or library.
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              mt={2}
              mb={2}
              style={{
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
              }}
            >
              <img
                src="https://backstage.io/assets/images/software-model-core-entities.drawio-51a40db7ca2e0d0026d99f519657677f.svg"
                alt="Backstage Core Entities Model: Components, APIs, and Resources"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                }}
              />
            </Box>
            <Typography
              variant="caption"
              display="block"
              align="center"
              color="textSecondary"
              style={{ marginBottom: '16px' }}
            >
              Core Backstage entities: Components, APIs, and Resources with
              their relationships
            </Typography>
            <Typography variant="body2" color="textSecondary">
              📋 Components are registered through catalog-info.yaml files in
              your repositories, enabling "infrastructure-as-code" management
              with all metadata versioned alongside your code.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="apis-content"
          id="apis-header"
        >
          <Typography>🔗 API Documentation & Discovery</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              Discover and explore APIs across Schibsted to understand service
              capabilities and plan integrations:
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🔍 Finding APIs You Need
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  <strong>Browse by Team:</strong> Filter APIs by the team that
                  maintains them
                </li>
                <li>
                  <strong>Search by Function:</strong> Use keywords like "user",
                  "payment", "search" to find relevant APIs
                </li>
                <li>
                  <strong>Check Dependencies:</strong> See which services your
                  component already uses
                </li>
              </ul>
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              📋 What You'll Find in API Docs
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  <strong>Interactive Documentation:</strong> Try API calls
                  directly in the browser
                </li>
                <li>
                  <strong>Schema Definitions:</strong> Request/response formats
                  and data types
                </li>
                <li>
                  <strong>Authentication:</strong> How to authenticate and
                  authorize requests
                </li>
                <li>
                  <strong>Usage Examples:</strong> Code snippets and integration
                  patterns
                </li>
              </ul>
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              📝 Adding Your API to the Catalog
            </Typography>
            <Typography variant="body2" paragraph>
              Register your API to make it discoverable by other teams:
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>Add an API entity to your catalog-info.yaml file</li>
                <li>Reference your OpenAPI specification (Swagger) file</li>
                <li>Include clear descriptions and contact information</li>
              </ul>
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '4px',
              }}
            >
              💡 <strong>Pro tip:</strong> Look at the "Provides" and "Consumes"
              sections of service pages to understand API relationships. This
              helps you identify which APIs are battle-tested and widely used
              across Schibsted.
            </Typography>

            <Typography variant="body2" style={{ marginTop: '12px' }}>
              <Link to="/api-docs">🔗 Explore Available APIs</Link>
              {' | '}
              <Link to="/catalog-import">📝 Register Your API</Link>
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="techdocs-content"
          id="techdocs-header"
        >
          <Typography>📚 TechDocs - Documentation Made Easy</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              Keep your technical documentation organized, up-to-date, and
              easily discoverable alongside your code in Schibsted:
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              📖 Why Use TechDocs?
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  <strong>Always Current:</strong> Documentation automatically
                  updates when you merge code changes
                </li>
                <li>
                  <strong>Easy to Find:</strong> Searchable through Backstage -
                  no more hunting through wikis
                </li>
                <li>
                  <strong>Team Ownership:</strong> Documentation lives with the
                  code, maintained by the team that knows it best
                </li>
                <li>
                  <strong>Consistent Format:</strong> Standard look and feel
                  across all Schibsted documentation
                </li>
              </ul>
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              🚀 Getting Started with TechDocs
            </Typography>
            <Typography variant="body2" paragraph>
              Setting up documentation for your component is straightforward:
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ol>
                <li>
                  <strong>Create docs folder:</strong> Add a `docs/` directory
                  to your repository
                </li>
                <li>
                  <strong>Write in Markdown:</strong> Create .md files with your
                  documentation
                </li>
                <li>
                  <strong>Add mkdocs.yml:</strong> Configure navigation and
                  settings
                </li>
                <li>
                  <strong>Update catalog-info.yaml:</strong> Reference your docs
                  in the component metadata
                </li>
              </ol>
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              📋 What to Document
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  <strong>Getting Started:</strong> How to set up and run your
                  service locally
                </li>
                <li>
                  <strong>API Reference:</strong> Endpoints, parameters, and
                  examples
                </li>
                <li>
                  <strong>Architecture:</strong> Key decisions, dependencies,
                  and data flow
                </li>
                <li>
                  <strong>Deployment:</strong> How to deploy, monitor, and
                  troubleshoot
                </li>
                <li>
                  <strong>Contributing:</strong> Guidelines for team members and
                  contributors
                </li>
              </ul>
            </Typography>
            <Typography variant="body2" style={{ marginTop: '12px' }}>
              <Link to="/docs">📚 Browse Documentation</Link>
              {' | '}
              <Link to="/create/templates/default/schibsted-backstage-catalog-info">
                📝 Use Template with TechDocs
              </Link>
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="help-content"
          id="help-header"
        >
          <Typography>🆘 Getting Help & Support</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" paragraph>
              Need help with Backstage or have questions about Schibsted's
              developer platform? Here's where to get support:
            </Typography>

            <Typography
              variant="h6"
              style={{ marginTop: '20px', marginBottom: '12px' }}
            >
              💬 Get Direct Help
            </Typography>
            <Typography
              variant="body2"
              component="div"
              style={{ marginBottom: '16px' }}
            >
              <ul>
                <li>
                  <strong>Slack Channel:</strong> Join{' '}
                  <a
                    href="https://schibsted-media.slack.com/archives/C0804HENT1P"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: theme.palette.primary.main }}
                  >
                    #backstage-support
                  </a>{' '}
                  for questions, troubleshooting, and announcements
                </li>
                <li>
                  <strong>Search Documentation:</strong> Use the search bar
                  above to find guides and API docs
                </li>
                <li>
                  <strong>Browse Examples:</strong> Look at existing components
                  in the catalog for inspiration
                </li>
                <li>
                  <strong>Check Team Pages:</strong> Find contact information
                  for teams that maintain services you're interested in
                </li>
              </ul>
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '4px',
              }}
            >
              💡 <strong>Pro tip:</strong> Before asking in #backstage-support,
              try searching the channel history - your question might already be
              answered!
            </Typography>

            <Typography variant="body2" style={{ marginTop: '12px' }}>
              <a
                href="https://schibsted-media.slack.com/archives/C0804HENT1P"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                💬 Join #backstage-support
              </a>
              {' | '}
              <a
                href="https://backstage.io/docs"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                📖 Official Backstage Docs
              </a>
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
